import { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Case, ImportStatus } from "src/types";
import { ImportFailed } from "src/components/ImportFailed";
import { ImportFinished } from "src/components/ImportFinished";
import { Pane } from "src/components/Pane";
import { usePrevious } from "src/hooks";
import { useCase } from "src/hooks/useCase";
import { PoiImportMutationData, usePoiImportMutation } from "src/hooks/usePoiImport";
import { PoiImportForm, PoiImportFormData } from "src/forms/PoiImportForm";

interface Props {}

export function PoiImportPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{
    caseId: string;
  }>();

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const { data: acase } = useCase(params.caseId || 0, {
    refetchInterval,
  });
  const previousImportStatus = usePrevious(acase?.poiImportStatus);
  const importMutation = usePoiImportMutation(params.caseId || 0);

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [acase]);
  useEffect(actUponFinishedImportStatus, [navigate, location, previousImportStatus, acase]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    if (acase?.poiImportStatus === ImportStatus.IMPORTING) {
      setRefetchInterval(1000);
    } else setRefetchInterval(false);
  }

  function actUponFinishedImportStatus() {
    // first check the previous import status
    if (previousImportStatus === undefined) return;
    if (previousImportStatus === ImportStatus.FINISHED) return;

    // if the new status is finished, act upon it
    if (acase?.poiImportStatus === ImportStatus.FINISHED) {
      navigate({ ...location }, { state: { action: "rerenderWMS" } });
    }
  }

  async function handleSave(data: PoiImportFormData) {
    if (!data.poiFile) {
      console.warn("No 'pointSourceFile' field submitted, this should be fixed.");
      return;
    }
    await importMutation.mutateAsync(data as PoiImportMutationData);
  }

  function handleClose() {
    navigate(`/cases/${params.caseId}/pois`);
  }

  // render the component
  return render();

  function render() {
    if (!acase) return null;

    return (
      <Pane width={400}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* TODO translate */}
            <Typography variant="h2">{t("label.bulkUploadPointsOfInterest")}</Typography>
          </Grid>

          {acase.poiImportStatus !== ImportStatus.IMPORTING && renderForm(acase)}

          {acase.poiImportStatus === ImportStatus.IMPORTING && (
            <Grid item xs={12}>
              <Typography variant="h2">Importing</Typography>
            </Grid>
          )}
        </Grid>
      </Pane>
    );
  }

  function renderForm(acase: Case) {
    return (
      <Grid item xs={12}>
        <PoiImportForm
          key={acase.poiImportStatus}
          data={{ poiFile: null }}
          onClose={handleClose}
          onSubmit={handleSave}
          isLoading={importMutation.isLoading}
          errors={importMutation.error?.response?.data}
          statusBody={
            <Grid item xs={12}>
              {renderStatusBody(acase)}
            </Grid>
          }
        />
      </Grid>
    );
  }

  function renderStatusBody(acase: Case) {
    if (acase.poiImportStatus === ImportStatus.FINISHED)
      return <ImportFinished importWarnings={acase.poiImportWarnings} />;
    if (acase.poiImportStatus === ImportStatus.FAILED) return <ImportFailed importErrors={acase.poiImportErrors} />;
    else return null;
  }
}
